import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, batch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import IconButton from '@material-ui/core/IconButton';

import AttachmentButton from './AttachmentButton';
import JournalGiphyEmoji from './JournalGiphyEmoji';

import iconSet from '../../../shared/images/teambuildr-selection.json';
import {
  ColorGray,
  ColorPrimary,
} from '../../../shared/GlobalStyles';

import { journalTracker } from '../../../shared/utils/amplitudeHelper';
import {
  clearEditComment,
  createJournalEntryComment,
  editJournalEntryComment,
  resetCommentError,
} from '../ducks/journalActions';

const CancelEditButton = styled('button')`
  background-color: transparent;
  border: none;
`;

const CommentBoxActions = styled('div')`
  align-items: center;
  display: flex;
  gap: 12px;
  height: 40px;
  justify-content: space-between;
`;

const CommentErrorText = styled('div')`
  color: #ff6600;
  font-size: 13px;
  line-height: 19.5px;
`;

const CommentInput = styled('input')`
  align-items: center;
  border: none;
  color: ${(props) => (props.value ? ColorPrimary : '#a3a3a3')};
  display: flex;
  font-size: 14px;
`;

const CommentInputContainer = styled('div')`
  align-items: center;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  color: ${ColorGray};
  display: flex;
  height: 40px;
  justify-content: space-between;
  padding: 4px 8px;
  width: 100%;
  @media screen and (max-width: 768px) {
    margin: 0 4px;
  }
`;

const CurrentlyEditing = styled('div')`
  color: ${ColorPrimary};
  font-size: 13px;
  font-style: italic;
  line-height: 19.5px;
`;

const EditingInformationDiv = styled('div')`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 2px;
`;

const GifButtonContainer = styled('div')`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const NewCommentBox = styled('div')`
  display: flex;
  flex-direction: column;
  font-family: 'Nunito Sans';
  justify-content: center;
  margin-top: 15px;
`;

const AddCommentBox = ({
  commentId,
  commentText,
  entryId,
  isEditing,
  setCommentsCollapsed,
  setCommentText,
}) => {
  const { commentUploadMedia, commentError } = useSelector(
    (state) => state.journal.data,
  );
  const { isLoadingComment } = useSelector((state) => state.journal.ui);
  const { accountCode } = useSelector((state) => state.auth.data.currentUser);
  const dispatch = useDispatch();

  const [tempCommentText, setTempCommentText] = useState(commentText);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const commentInputRef = useRef();

  const hasError = commentError?.entryId && commentError?.entryId === entryId;
  const hasMedia = commentUploadMedia?.entryId === entryId
    && commentUploadMedia?.commentId === commentId;
  const submittedMediaId = hasMedia ? commentUploadMedia?.media?.id : 0;

  const submitButtonDisabled = (!tempCommentText?.trim().length && !hasMedia)
    || isSubmitting
    || isLoadingComment;

  const handleCancelEdit = () => {
    batch(() => {
      dispatch(clearEditComment());
      dispatch(resetCommentError());
    });
    setTempCommentText(commentText);
    journalTracker('Journal entry', 'Comment edit cancelled');
  };

  const handleInputChange = (e) => {
    e.preventDefault();
    setTempCommentText(e.target.value);
    if (setCommentText) {
      setCommentText(e.target.value);
    }
  };

  const handleSubmitComment = () => {
    if (submitButtonDisabled) {
      return;
    }

    setIsSubmitting(true);

    const values = {
      submissionObj: {
        commentText: tempCommentText,
        mediaId: submittedMediaId,
      },
      entryId,
    };
    if (isEditing) {
      values.commentId = commentId;
      dispatch(editJournalEntryComment(accountCode, values));
      journalTracker('Journal entry', 'Comment edited');
    } else {
      dispatch(createJournalEntryComment(accountCode, values));
      if (setCommentText) {
        setCommentText('');
      }
      setCommentsCollapsed(false);
      journalTracker('Journal entry', 'Comment added');
    }
    setTempCommentText('');
  };

  const handleSubmitOnEnter = (e) => {
    const { key, shiftKey } = e;
    if (key === 'Enter' && !shiftKey) {
      e.preventDefault();
      handleSubmitComment();
    }
  };

  useEffect(() => {
    if (isEditing && commentInputRef.current) {
      commentInputRef.current.focus();
    }
  }, [isEditing]);

  useEffect(() => {
    setTempCommentText(commentText);
  }, [commentText]);

  useEffect(() => {
    if (!isLoadingComment) {
      setIsSubmitting(false);
    }
  }, [isLoadingComment]);

  const renderEditingInfo = () => {
    if (!isEditing) return null;

    return (
      <EditingInformationDiv>
        <CurrentlyEditing>Currently Editing</CurrentlyEditing>
        <CancelEditButton
          aria-label='Cancel editing comment'
          onClick={handleCancelEdit}
        >
          Cancel
        </CancelEditButton>
      </EditingInformationDiv>
    );
  };
  const renderSendButton = () => (
    <IconButton
      aria-label='Submit comment'
      disabled={submitButtonDisabled}
      onClick={handleSubmitComment}
      size='small'
    >
      <IcomoonReact
        color={submitButtonDisabled ? '#9e9e9e' : '#444444'}
        iconSet={iconSet}
        size={15}
        icon='send-alt'
      />
    </IconButton>
  );

  const renderCommentInput = () => (
    <CommentInputContainer>
      <CommentInput
        placeholder='Reply to post'
        aria-label='Write new comment'
        type='text'
        value={tempCommentText}
        onChange={handleInputChange}
        onKeyDown={handleSubmitOnEnter}
        ref={commentInputRef}
      />
      <GifButtonContainer>
        <JournalGiphyEmoji
          newCommentText={tempCommentText}
          setNewCommentText={setTempCommentText}
          entryId={entryId}
          commentId={commentId}
        />
        {renderSendButton()}
      </GifButtonContainer>
    </CommentInputContainer>
  );

  const renderCommentActions = () => (
    <CommentBoxActions>
      <AttachmentButton entryId={entryId} commentId={commentId} />
      {renderCommentInput()}
    </CommentBoxActions>
  );

  const renderError = () => {
    if (!hasError) return null;

    return (
      <CommentErrorText>
        *
        {' '}
        {commentError?.err?.response?.headers?.message}
      </CommentErrorText>
    );
  };

  return (
    <NewCommentBox>
      {renderEditingInfo()}
      {renderCommentActions()}
      {renderError()}
    </NewCommentBox>
  );
};

AddCommentBox.propTypes = {
  commentId: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(null)])
    .isRequired,
  commentText: PropTypes.string,
  entryId: PropTypes.number.isRequired,
  isEditing: PropTypes.bool.isRequired,
  setCommentText: PropTypes.func.isRequired,
  setCommentsCollapsed: PropTypes.func.isRequired,
};

AddCommentBox.defaultProps = {
  commentText: '',
};

export default AddCommentBox;
